import React, { useState, useMemo } from "react";
import { BarChart3, Plus, Trash2, AlertTriangle } from "lucide-react";

const MONTH_LABELS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const CHART_HEIGHT = 220;

function formatCurrency(value) {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(value);
}

function formatCurrencyShort(value) {
  const num = Number(value || 0);
  const abs = Math.abs(num);
  const sign = num < 0 ? "-" : "";
  const intPart = Math.round(abs).toString();
  const groupedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${sign}${groupedInt} €`;
}

function parseAmount(str) {
  if (!str) return 0;
  const cleaned = str.replace(/\s/g, "").replace(",", ".");
  return parseFloat(cleaned) || 0;
}

export default function FatturatoSimulator({
  invoices,
  selectedYear,
  incassiAnno,
  profitabilityCoefficient,
  aliquota,
  inpsRate,
  contributiPrecedenti,
  soglia = 85000,
  darkMode = false,
}) {
  const [fattureSimulate, setFattureSimulate] = useState([]);

  // Calcola incassi mensili reali
  const incassiMensili = useMemo(() => {
    const mensili = Array(12).fill(0);
    invoices.forEach((inv) => {
      if (!inv.paid) return;
      const dateToUse = inv.paymentDate || inv.issueDate;
      if (!dateToUse) return;
      const date = new Date(dateToUse);
      if (date.getFullYear() !== selectedYear) return;
      mensili[date.getMonth()] += Number(inv.grossAmount) || 0;
    });
    return mensili;
  }, [invoices, selectedYear]);

  // Calcola incassi mensili simulati
  const simulatiMensili = useMemo(() => {
    const mensili = Array(12).fill(0);
    fattureSimulate.forEach((f) => {
      const importo = parseAmount(f.importo);
      if (importo > 0 && f.mese >= 1 && f.mese <= 12) {
        mensili[f.mese - 1] += importo;
      }
    });
    return mensili;
  }, [fattureSimulate]);

  const totaleSimulato = simulatiMensili.reduce((a, b) => a + b, 0);
  const nuovoTotale = incassiAnno + totaleSimulato;

  // Calcolo tasse - situazione attuale
  const tasseAttuali = useMemo(() => {
    const imponibile = incassiAnno * profitabilityCoefficient;
    const inps = imponibile * (inpsRate / 100);
    const imponibileNetto = Math.max(0, imponibile - contributiPrecedenti);
    const imposta = imponibileNetto * (aliquota / 100);
    return { imponibile, inps, imposta, totale: inps + imposta, netto: incassiAnno - inps - imposta };
  }, [incassiAnno, profitabilityCoefficient, inpsRate, aliquota, contributiPrecedenti]);

  // Calcolo tasse - con simulate
  const tasseSimulate = useMemo(() => {
    const imponibile = nuovoTotale * profitabilityCoefficient;
    const inps = imponibile * (inpsRate / 100);
    const imponibileNetto = Math.max(0, imponibile - contributiPrecedenti);
    const imposta = imponibileNetto * (aliquota / 100);
    return { imponibile, inps, imposta, totale: inps + imposta, netto: nuovoTotale - inps - imposta };
  }, [nuovoTotale, profitabilityCoefficient, inpsRate, aliquota, contributiPrecedenti]);

  // % soglia (soglia ragguagliata ai giorni di attività nel primo anno)
  const percentualeAttuale = soglia > 0 ? (incassiAnno / soglia) * 100 : 0;
  const percentualeDopo = soglia > 0 ? (nuovoTotale / soglia) * 100 : 0;
  const margineDopo = soglia - nuovoTotale;

  // Mese superamento soglia
  const meseSuperamento = useMemo(() => {
    let cumulo = 0;
    for (let i = 0; i < 12; i++) {
      cumulo += incassiMensili[i] + simulatiMensili[i];
      if (cumulo > soglia) return i;
    }
    return -1;
  }, [incassiMensili, simulatiMensili, soglia]);

  // Scala Y per il grafico
  const maxValue = useMemo(() => {
    let max = 0;
    for (let i = 0; i < 12; i++) {
      const total = incassiMensili[i] + simulatiMensili[i];
      if (total > max) max = total;
    }
    return Math.max(max * 1.2, 100);
  }, [incassiMensili, simulatiMensili]);

  const getBarHeight = (value) => {
    if (value <= 0) return 0;
    return Math.max((value / maxValue) * CHART_HEIGHT, 3);
  };

  // Cumulativo per linea soglia
  const cumulativo = useMemo(() => {
    return incassiMensili.reduce((acc, val, idx) => {
      const prev = idx > 0 ? acc[idx - 1] : 0;
      acc.push(prev + val + simulatiMensili[idx]);
      return acc;
    }, []);
  }, [incassiMensili, simulatiMensili]);

  const addFattura = () => {
    const meseCorrente = new Date().getMonth() + 1;
    setFattureSimulate((prev) => [
      ...prev,
      { id: Date.now(), importo: "", mese: meseCorrente },
    ]);
  };

  const removeFattura = (id) => {
    setFattureSimulate((prev) => prev.filter((f) => f.id !== id));
  };

  const updateFattura = (id, field, value) => {
    setFattureSimulate((prev) =>
      prev.map((f) => (f.id === id ? { ...f, [field]: value } : f))
    );
  };

  const kpiRows = [
    {
      label: "Incassi totali",
      attuale: incassiAnno,
      simulato: nuovoTotale,
    },
    {
      label: "Imponibile",
      attuale: tasseAttuali.imponibile,
      simulato: tasseSimulate.imponibile,
    },
    {
      label: `Imposta ${aliquota}%`,
      attuale: tasseAttuali.imposta,
      simulato: tasseSimulate.imposta,
    },
    {
      label: "Contributi INPS",
      attuale: tasseAttuali.inps,
      simulato: tasseSimulate.inps,
    },
    {
      label: "Totale tasse",
      attuale: tasseAttuali.totale,
      simulato: tasseSimulate.totale,
      isBold: true,
    },
    {
      label: "Netto disponibile",
      attuale: tasseAttuali.netto,
      simulato: tasseSimulate.netto,
      isBold: true,
      isNetto: true,
    },
  ];

  return (
    <div className="space-y-6">
      {/* 1. Input fatture ipotettiche */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3
            className={`text-sm font-semibold ${
              darkMode ? "text-white" : "text-neutral-900"
            }`}
          >
            Fatture ipotettiche
          </h3>
          <button
            onClick={addFattura}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-forfy-500 text-white hover:bg-forfy-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Aggiungi
          </button>
        </div>

        {fattureSimulate.length === 0 ? (
          <div
            className={`text-center py-6 rounded-xl border-2 border-dashed ${
              darkMode
                ? "border-neutral-700 text-neutral-500"
                : "border-neutral-300 text-neutral-400"
            }`}
          >
            <BarChart3 className="w-6 h-6 mx-auto mb-1.5 opacity-50" />
            <p className="text-sm">
              Aggiungi fatture ipotettiche per simulare
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {fattureSimulate.map((f) => (
              <div
                key={f.id}
                className={`flex items-center gap-3 p-3 rounded-xl border ${
                  darkMode
                    ? "border-neutral-800 bg-neutral-800/50"
                    : "border-neutral-200 bg-neutral-50"
                }`}
              >
                <div className="flex-1 relative">
                  <span
                    className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm ${
                      darkMode ? "text-neutral-500" : "text-neutral-500"
                    }`}
                  >
                    €
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={f.importo}
                    onChange={(e) =>
                      updateFattura(
                        f.id,
                        "importo",
                        e.target.value.replace(/[^0-9,.]/g, "")
                      )
                    }
                    placeholder="0,00"
                    className={`w-full pl-8 pr-3 py-2 border rounded-lg text-sm ${
                      darkMode
                        ? "border-neutral-700 bg-neutral-800 text-white"
                        : "border-neutral-300 bg-white text-neutral-900"
                    }`}
                  />
                </div>
                <select
                  value={f.mese}
                  onChange={(e) =>
                    updateFattura(f.id, "mese", Number(e.target.value))
                  }
                  className={`px-3 py-2 border rounded-lg text-sm ${
                    darkMode
                      ? "border-neutral-700 bg-neutral-800 text-white"
                      : "border-neutral-300 bg-white text-neutral-900"
                  }`}
                >
                  {MONTH_LABELS.map((label, idx) => (
                    <option key={idx} value={idx + 1}>
                      {label}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => removeFattura(f.id)}
                  className={`p-2 rounded-lg transition-colors ${
                    darkMode
                      ? "text-neutral-500 hover:text-red-400 hover:bg-red-900/30"
                      : "text-neutral-400 hover:text-red-600 hover:bg-red-50"
                  }`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 2. KPI confronto */}
      {totaleSimulato > 0 && (
        <>
          <div
            className={`rounded-xl border overflow-hidden ${
              darkMode ? "border-neutral-800" : "border-neutral-200"
            }`}
          >
            <table className="w-full text-sm">
              <thead>
                <tr
                  className={
                    darkMode ? "bg-neutral-800" : "bg-neutral-100"
                  }
                >
                  <th
                    className={`px-4 py-2.5 text-left font-medium ${
                      darkMode ? "text-neutral-300" : "text-neutral-700"
                    }`}
                  >
                    KPI
                  </th>
                  <th
                    className={`px-4 py-2.5 text-right font-medium ${
                      darkMode ? "text-neutral-300" : "text-neutral-700"
                    }`}
                  >
                    Attuale
                  </th>
                  <th
                    className={`px-4 py-2.5 text-right font-medium ${
                      darkMode ? "text-forfy-300" : "text-forfy-600"
                    }`}
                  >
                    Simulato
                  </th>
                  <th
                    className={`px-4 py-2.5 text-right font-medium ${
                      darkMode ? "text-neutral-300" : "text-neutral-700"
                    }`}
                  >
                    Delta
                  </th>
                </tr>
              </thead>
              <tbody>
                {kpiRows.map((row, idx) => {
                  const delta = row.simulato - row.attuale;
                  const isPositiveDelta = row.isNetto
                    ? delta >= 0
                    : delta <= 0;
                  return (
                    <tr
                      key={row.label}
                      className={
                        darkMode
                          ? idx % 2 === 0
                            ? "bg-neutral-900"
                            : "bg-neutral-800/50"
                          : idx % 2 === 0
                          ? "bg-white"
                          : "bg-neutral-50"
                      }
                    >
                      <td
                        className={`px-4 py-2.5 ${
                          row.isBold ? "font-semibold" : ""
                        } ${
                          darkMode
                            ? "text-neutral-300"
                            : "text-neutral-700"
                        }`}
                      >
                        {row.label}
                      </td>
                      <td
                        className={`px-4 py-2.5 text-right tabular-nums ${
                          row.isBold ? "font-semibold" : ""
                        } ${
                          darkMode
                            ? "text-neutral-400"
                            : "text-neutral-600"
                        }`}
                      >
                        {formatCurrency(row.attuale)}
                      </td>
                      <td
                        className={`px-4 py-2.5 text-right tabular-nums ${
                          row.isBold ? "font-bold" : "font-medium"
                        } ${
                          row.isNetto
                            ? darkMode
                              ? "text-emerald-400"
                              : "text-emerald-600"
                            : darkMode
                            ? "text-white"
                            : "text-neutral-900"
                        }`}
                      >
                        {formatCurrency(row.simulato)}
                      </td>
                      <td
                        className={`px-4 py-2.5 text-right tabular-nums font-medium ${
                          isPositiveDelta
                            ? darkMode
                              ? "text-emerald-400"
                              : "text-emerald-600"
                            : darkMode
                            ? "text-red-400"
                            : "text-red-600"
                        }`}
                      >
                        {delta >= 0 ? "+" : ""}
                        {formatCurrency(delta)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Soglia info sotto tabella */}
            <div
              className={`px-4 py-3 border-t ${
                darkMode
                  ? "border-neutral-800 bg-neutral-800/50"
                  : "border-neutral-200 bg-neutral-50"
              }`}
            >
              <div className="flex items-center justify-between text-sm">
                <span
                  className={
                    darkMode ? "text-neutral-400" : "text-neutral-600"
                  }
                >
                  Soglia 85k
                </span>
                <div className="flex items-center gap-4">
                  <span
                    className={`tabular-nums ${
                      darkMode ? "text-neutral-500" : "text-neutral-500"
                    }`}
                  >
                    {percentualeAttuale.toFixed(0)}%
                  </span>
                  <span
                    className={darkMode ? "text-neutral-600" : "text-neutral-300"}
                  >
                    →
                  </span>
                  <span
                    className={`font-semibold tabular-nums ${
                      percentualeDopo > 100
                        ? darkMode
                          ? "text-red-400"
                          : "text-red-600"
                        : percentualeDopo > 88
                        ? darkMode
                          ? "text-amber-400"
                          : "text-amber-600"
                        : darkMode
                        ? "text-emerald-400"
                        : "text-emerald-600"
                    }`}
                  >
                    {percentualeDopo.toFixed(0)}%
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between text-sm mt-1">
                <span
                  className={
                    darkMode ? "text-neutral-400" : "text-neutral-600"
                  }
                >
                  Margine residuo
                </span>
                <span
                  className={`font-medium tabular-nums ${
                    margineDopo < 0
                      ? darkMode
                        ? "text-red-400"
                        : "text-red-600"
                      : darkMode
                      ? "text-emerald-400"
                      : "text-emerald-600"
                  }`}
                >
                  {formatCurrency(margineDopo)}
                </span>
              </div>
              {meseSuperamento >= 0 && (
                <div className="flex items-center justify-between text-sm mt-1">
                  <span
                    className={
                      darkMode ? "text-neutral-400" : "text-neutral-600"
                    }
                  >
                    Mese superamento
                  </span>
                  <span
                    className={`font-semibold ${
                      darkMode ? "text-red-400" : "text-red-600"
                    }`}
                  >
                    {MONTH_LABELS[meseSuperamento]} {selectedYear}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* 3. Grafico barre mensili */}
          <div
            className={`rounded-xl border p-4 ${
              darkMode
                ? "border-neutral-800 bg-neutral-800/30"
                : "border-neutral-200 bg-neutral-50"
            }`}
          >
            {/* Legenda */}
            <div className="flex items-center gap-4 mb-3 text-xs">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-forfy-500" />
                <span
                  className={
                    darkMode ? "text-neutral-500" : "text-neutral-600"
                  }
                >
                  Reale
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-indigo-400" />
                <span
                  className={
                    darkMode ? "text-neutral-500" : "text-neutral-600"
                  }
                >
                  Simulato
                </span>
              </div>
              <div className="flex items-center gap-1.5 ml-auto">
                <div
                  className={`w-6 h-0.5 ${
                    darkMode ? "bg-red-500" : "bg-red-400"
                  }`}
                  style={{
                    backgroundImage:
                      "repeating-linear-gradient(90deg, currentColor 0 4px, transparent 4px 8px)",
                  }}
                />
                <span
                  className={
                    darkMode ? "text-neutral-500" : "text-neutral-600"
                  }
                >
                  Soglia 85k
                </span>
              </div>
            </div>

            {/* Barre */}
            <div className="relative flex gap-1 px-1">
              {/* Linea soglia 85k (relativa al cumulativo) */}
              {(() => {
                const sogliaMensile = soglia / 12;
                const percentualeSoglia = Math.min(
                  (sogliaMensile / maxValue) * 100,
                  100
                );
                return percentualeSoglia < 100 ? (
                  <div
                    className="absolute left-0 right-0 border-t-2 border-dashed border-red-400/60 pointer-events-none z-10"
                    style={{
                      bottom: `${(percentualeSoglia / 100) * CHART_HEIGHT + 20}px`,
                    }}
                  />
                ) : null;
              })()}

              {MONTH_LABELS.map((label, idx) => {
                const reale = incassiMensili[idx];
                const simulato = simulatiMensili[idx];
                const cum = cumulativo[idx];
                const isOverThreshold = cum > soglia;

                return (
                  <div
                    key={label}
                    className="flex-1 flex flex-col items-center group relative"
                  >
                    {/* Barre */}
                    <div
                      className="w-full flex items-end justify-center gap-0.5"
                      style={{ height: `${CHART_HEIGHT}px` }}
                    >
                      {/* Barra reale */}
                      <div
                        className={`w-2.5 rounded-t transition-all ${
                          isOverThreshold ? "bg-red-500" : "bg-forfy-500"
                        }`}
                        style={{
                          height: `${getBarHeight(reale)}px`,
                        }}
                      />
                      {/* Barra simulata */}
                      <div
                        className="w-2.5 bg-indigo-400 rounded-t transition-all"
                        style={{
                          height: `${getBarHeight(simulato)}px`,
                        }}
                      />
                    </div>

                    {/* Label mese */}
                    <div
                      className={`h-5 flex items-center justify-center text-[10px] ${
                        darkMode
                          ? "text-neutral-500"
                          : "text-neutral-500"
                      }`}
                    >
                      {label}
                    </div>

                    {/* Tooltip */}
                    {(reale > 0 || simulato > 0) && (
                      <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 z-20 bg-neutral-900 text-white text-xs rounded-lg p-2 whitespace-nowrap shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity">
                        <div className="font-medium mb-1">
                          {label} {selectedYear}
                        </div>
                        <div className="flex justify-between gap-3">
                          <span className="text-forfy-300">Reale:</span>
                          <span>{formatCurrencyShort(reale)}</span>
                        </div>
                        {simulato > 0 && (
                          <div className="flex justify-between gap-3">
                            <span className="text-indigo-300">
                              Simulato:
                            </span>
                            <span>{formatCurrencyShort(simulato)}</span>
                          </div>
                        )}
                        <div className="flex justify-between gap-3 border-t border-neutral-700 mt-1 pt-1">
                          <span className="text-neutral-400">
                            Cumul.:
                          </span>
                          <span
                            className={
                              isOverThreshold ? "text-red-400" : ""
                            }
                          >
                            {formatCurrencyShort(cum)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Disclaimer */}
      <div
        className={`flex gap-3 p-3 rounded-lg ${
          darkMode ? "bg-amber-900/20" : "bg-amber-50"
        }`}
      >
        <AlertTriangle
          className={`w-5 h-5 flex-shrink-0 ${
            darkMode ? "text-amber-400" : "text-amber-600"
          }`}
        />
        <p
          className={`text-xs ${
            darkMode ? "text-amber-300" : "text-amber-800"
          }`}
        >
          Simulazione indicativa basata sui parametri attuali. I calcoli
          non considerano deduzioni aggiuntive o variazioni normative.
          Consulta il commercialista per decisioni importanti.
        </p>
      </div>
    </div>
  );
}
